import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, Mail, Download, Lock, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Shell, TopBar } from "../components/Shell";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { CLINIC } from "../lib/data";
import { api, API } from "../lib/api";

export default function Control() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("sid");
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const pdfUrl = `${API}/screenings/${sid}/report.pdf`;
  const message = `Hello Muse Charitable Dental Clinic, here is my AI screening report from Muse Dental AI: ${pdfUrl}`;

  const grantConsent = async () => {
    await api.post(`/screenings/${sid}/share`, { consent: true }).catch(() => {});
  };

  const shareWhatsApp = async () => {
    await grantConsent();
    window.open(`https://wa.me/${CLINIC.phoneDial}?text=${encodeURIComponent(message)}`, "_blank");
  };
  const shareEmail = async () => {
    await grantConsent();
    const subject = encodeURIComponent("My Muse Dental AI screening report");
    window.location.href = `mailto:${CLINIC.email}?subject=${subject}&body=${encodeURIComponent(message)}`;
  };
  const downloadPdf = () => window.open(pdfUrl, "_blank");

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/screenings/${sid}`);
      toast.success("Your screening and photos were deleted.");
      navigate("/", { replace: true });
    } catch (e) { toast.error("Delete failed."); }
    finally { setDeleting(false); setDelOpen(false); }
  };

  const items = [
    { testid: "share-whatsapp", icon: MessageCircle, title: "Send to Muse via WhatsApp", desc: "Share your report with the clinic on WhatsApp.", onClick: shareWhatsApp, primary: true },
    { testid: "share-email", icon: Mail, title: "Send to Muse via Email", desc: `Email your report to ${CLINIC.email}.`, onClick: shareEmail },
    { testid: "download-pdf", icon: Download, title: "Download PDF report", desc: "Save a branded copy for yourself.", onClick: downloadPdf },
    { testid: "keep-private", icon: Lock, title: "Keep private", desc: "Do nothing — your report stays only with you.", onClick: () => navigate("/") },
  ];

  return (
    <Shell header={<TopBar title="Share / Save" onBack={() => navigate(`/report?sid=${sid}`)} />}>
      <div className="px-6 py-5 flex flex-col gap-5 pb-10">
        <div className="rounded-2xl brand-gradient-soft p-5 text-white flex items-center gap-3 mint-glow">
          <CheckCircle2 className="w-7 h-7 text-[#8FE3BE]" />
          <div>
            <p className="font-head font-bold text-base">Your branded report is ready</p>
            <p className="text-[12.5px] text-white/80">Choose how you'd like to keep or share it.</p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {items.map((it) => (
            <button key={it.testid} data-testid={it.testid} onClick={it.onClick}
              className={`tap flex items-start gap-3 rounded-2xl border p-4 text-left ${it.primary ? "border-primary/30 bg-accent/60" : "border-border bg-card"}`}>
              <div className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center shrink-0"><it.icon className="w-5 h-5 text-primary" /></div>
              <div className="flex-1">
                <p className="font-head font-semibold text-[15px]">{it.title}</p>
                <p className="text-[12.5px] text-muted-foreground">{it.desc}</p>
              </div>
            </button>
          ))}

          <button data-testid="delete-screening" onClick={() => setDelOpen(true)}
            className="tap flex items-center justify-center gap-2 rounded-2xl border border-destructive/40 text-destructive p-4 font-semibold">
            <Trash2 className="w-4 h-4" /> Delete screening
          </button>
        </div>

        <p className="text-[11.5px] text-muted-foreground leading-relaxed">
          By sharing, you consent to sending your screening details (patient info, complaint, photos and AI findings) to Muse Charitable Dental Clinic. This is an educational screening, not a confirmed diagnosis.
        </p>
      </div>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="max-w-md rounded-3xl" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head">Delete this screening?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes your report and all uploaded photos. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={(e) => { e.preventDefault(); doDelete(); }}
              className="rounded-2xl bg-destructive hover:bg-destructive/90">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
